import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Image as ImageIcon, Loader2, CheckCircle2, AlertCircle,
  ExternalLink, Upload,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * AdminProductImageTool — sibling to AdminAvatarTool, but for product
 * thumbnails. Use when a creator's product image isn't taking effect
 * (broken ephemeral URL, unresponsive creator, or a critical listing
 * where we want to manually install their official logo).
 *
 * Flow:
 *   1. Look up the product by product_id OR slug
 *   2. Provide an image source (paste URL or upload — same /api/upload/image
 *      → GridFS path the rest of the platform uses)
 *   3. Pick replace (wipe gallery) or prepend (keep gallery, just swap thumb)
 *   4. POST /api/admin/products/set-image
 */
export default function AdminProductImageTool() {
  const [lookupMode, setLookupMode] = useState('product_id'); // product_id | slug
  const [lookupValue, setLookupValue] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [mode, setMode] = useState('replace');
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError] = useState('');

  const applyImage = async () => {
    if (!lookupValue.trim()) {
      toast.error('Enter the product_id or slug first');
      return;
    }
    if (!imageUrl.trim()) {
      toast.error('Provide an image URL (or upload one)');
      return;
    }
    setApplying(true);
    setLastError('');
    try {
      const payload = { image_url: imageUrl.trim(), mode };
      payload[lookupMode] = lookupValue.trim();
      const res = await axios.post(
        `${API}/api/admin/products/set-image`,
        payload,
        { headers: auth() },
      );
      setLastResult(res.data);
      toast.success(`Image applied to ${res.data.name || res.data.product_id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed';
      setLastError(msg);
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/upload/image`, fd, {
        headers: { ...auth() },
      });
      const url = res.data?.url;
      if (!url) throw new Error('Upload returned no URL');
      const absoluteUrl = url.startsWith('http') ? url : `${API}${url}`;
      setImageUrl(absoluteUrl);
      toast.success('Uploaded — now click Apply Image');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-product-image-tool">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#00F0FF]" />
              Product Image Tool
            </h1>
          </div>
          <Link
            to="/admin/avatar-tool"
            className="text-xs text-neutral-400 hover:text-[#00F0FF] underline underline-offset-2"
            data-testid="link-to-avatar-tool"
          >
            Avatar tool →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <h2 className="font-bold text-base mb-1">Force-set a product's primary image</h2>
          <p className="text-xs text-neutral-400 leading-relaxed">
            Use when a creator's product thumbnail won't take effect (broken ephemeral
            URL, unresponsive creator, etc.). Look up the product by ID or slug, upload
            or paste an image, then choose whether to replace the gallery or keep it
            and just swap the thumbnail.
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-4">
          {/* Lookup */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-2 block">
              1. Find the product
            </label>
            <div className="flex gap-1 mb-3 p-1 rounded-lg bg-white/[0.04] border border-white/10 w-fit">
              {[
                { v: 'product_id', l: 'Product ID' },
                { v: 'slug', l: 'Slug' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`product-lookup-mode-${opt.v}`}
                  onClick={() => { setLookupMode(opt.v); setLookupValue(''); }}
                  className={`px-3 py-1 text-xs rounded font-semibold transition-colors ${
                    lookupMode === opt.v
                      ? 'bg-[#00F0FF] text-black'
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={lookupValue}
              onChange={(e) => setLookupValue(e.target.value)}
              placeholder={lookupMode === 'product_id' ? 'product_xxxxxxxxxxxx' : 'product-slug-here'}
              data-testid="product-lookup-value-input"
              className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono"
            />
          </div>

          {/* Image source */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-2 block">
              2. Image source
            </label>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/product.png  (or upload below)"
              data-testid="product-image-url-input"
              className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono mb-2"
            />

            <label
              htmlFor="product-image-upload-input"
              data-testid="product-image-upload-trigger"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer font-semibold"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5" />
                  Upload image
                </>
              )}
              <input
                id="product-image-upload-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                data-testid="product-image-upload-input"
              />
            </label>

            {imageUrl && (
              <div className="mt-3 flex items-center gap-3 p-2 rounded-lg bg-black/40 border border-white/10">
                <img
                  src={imageUrl}
                  alt="preview"
                  data-testid="product-image-preview-img"
                  className="w-14 h-14 rounded-lg object-cover border border-white/10 bg-neutral-900"
                  onError={(e) => { e.target.style.opacity = 0.3; }}
                />
                <div className="text-xs text-neutral-400 truncate font-mono flex-1">{imageUrl}</div>
              </div>
            )}
          </div>

          {/* Mode */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-2 block">
              3. Apply mode
            </label>
            <div className="flex gap-2">
              {[
                { v: 'replace', l: 'Replace gallery', d: 'Wipes the existing images array and sets just this one.' },
                { v: 'prepend', l: 'Prepend (keep gallery)', d: 'Pushes this to the front; rest stays as-is.' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`mode-${opt.v}`}
                  onClick={() => setMode(opt.v)}
                  className={`flex-1 text-left rounded-lg border p-3 transition-colors ${
                    mode === opt.v
                      ? 'border-[#00F0FF]/60 bg-[#00F0FF]/[0.06]'
                      : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                >
                  <div className="text-sm font-bold mb-0.5">{opt.l}</div>
                  <div className="text-[11px] text-neutral-400 leading-snug">{opt.d}</div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={applyImage}
            disabled={applying || !lookupValue || !imageUrl}
            data-testid="apply-product-image-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] text-black font-bold py-2.5 rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Applying…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Apply product image
              </>
            )}
          </button>
        </div>

        {lastResult && (
          <div
            className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-5"
            data-testid="product-image-result"
          >
            <div className="flex items-center gap-2 mb-3 text-emerald-300 font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Applied successfully — {lastResult.name}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Product ID" value={lastResult.product_id} mono />
              <Field label="Slug" value={lastResult.slug} mono />
              <Field label="Creator ID" value={lastResult.creator_id} mono />
              <Field label="Mode" value={`${lastResult.mode} · ${lastResult.modified} row(s)`} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(lastResult.images || []).slice(0, 4).map((u) => (
                <img
                  key={u}
                  src={u.startsWith('http') ? u : `${API}${u}`}
                  alt="applied"
                  className="w-12 h-12 rounded-lg object-cover border border-emerald-400/30"
                />
              ))}
              {lastResult.slug && (
                <a
                  href={`/p/${lastResult.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-emerald-300 inline-flex items-center gap-1 underline ml-auto"
                >
                  View product page <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        )}

        {lastError && (
          <div
            className="rounded-xl border border-red-400/30 bg-red-400/[0.04] p-5"
            data-testid="product-image-error"
          >
            <div className="flex items-center gap-2 mb-1 text-red-300 font-bold text-sm">
              <AlertCircle className="w-4 h-4" />
              Could not apply
            </div>
            <div className="text-xs text-red-200/80">{lastError}</div>
          </div>
        )}
      </main>
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div className="rounded-lg bg-black/30 border border-white/10 p-2">
    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">{label}</div>
    <div className={`text-white text-xs truncate ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
  </div>
);
